import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { authAPI, UserProfileUpdate } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, onSaved }) => {
  const { user, updateUser } = useAuthStore();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      setFetchingProfile(true);
      authAPI.getProfile()
        .then((profile) => {
          setFormData({
            first_name: profile.first_name || '',
            last_name: profile.last_name || '',
            address_line1: profile.address_line1 || '',
            address_line2: profile.address_line2 || '',
            city: profile.city || '',
            state: profile.state || '',
            zip_code: profile.zip_code || '',
            country: profile.country || '',
            phone: profile.phone || '',
          });
        })
        .catch(() => {
          // Fall back to store data
          if (user) {
            setFormData({
              first_name: user.first_name || '',
              last_name: user.last_name || '',
              address_line1: user.address_line1 || '',
              address_line2: user.address_line2 || '',
              city: user.city || '',
              state: user.state || '',
              zip_code: user.zip_code || '',
              country: user.country || '',
              phone: user.phone || '',
            });
          }
        })
        .finally(() => setFetchingProfile(false));
    }
  }, [isOpen, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const updateData: UserProfileUpdate = {};
      for (const [key, value] of Object.entries(formData)) {
        (updateData as any)[key] = value || undefined;
      }

      const updatedUser = await authAPI.updateProfile(updateData);
      updateUser(updatedUser);
      onClose();
      onSaved?.();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="My Profile" size="lg">
      {fetchingProfile ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name"
              name="first_name"
              type="text"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="First name"
            />
            <Input
              label="Last Name"
              name="last_name"
              type="text"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Last name"
            />
          </div>

          <Input
            label="Address Line 1"
            name="address_line1"
            type="text"
            value={formData.address_line1}
            onChange={handleChange}
            placeholder="Street address"
          />

          <Input
            label="Address Line 2"
            name="address_line2"
            type="text"
            value={formData.address_line2}
            onChange={handleChange}
            placeholder="Apt, suite, unit, etc. (optional)"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="City"
              name="city"
              type="text"
              value={formData.city}
              onChange={handleChange}
              placeholder="City"
            />
            <Input
              label="State"
              name="state"
              type="text"
              value={formData.state}
              onChange={handleChange}
              placeholder="State"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="ZIP Code"
              name="zip_code"
              type="text"
              value={formData.zip_code}
              onChange={handleChange}
              placeholder="ZIP code"
            />
            <Input
              label="Country"
              name="country"
              type="text"
              value={formData.country}
              onChange={handleChange}
              placeholder="Country"
            />
          </div>

          <Input
            label="Phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Phone number"
          />

          <div className="flex space-x-3 pt-2">
            <Button type="button" variant="secondary" onClick={onClose} fullWidth>
              Close
            </Button>
            <Button type="submit" fullWidth disabled={loading}>
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};
